import { useCallback, useState } from 'react';

import { useEntitySchema, useStack9 } from '@april9au/stack9-react';
import { ep } from '@april9au/stack9-sdk';
import * as ui from '@april9au/stack9-ui';
import { message } from 'antd';

import { PERMISSION_SET_FIELDS } from './permissionSetFields';

const Drawer = ui.S9Drawer.default;
const Form = ui.S9Form.default;
const Fieldset = ui.S9Fieldset.default;
const S9Button = ui.S9Button.default;
const S9Space = ui.Space;
const { useForm } = ui.S9Form;
const { ScreenProvider, useScreen } = ui;

const PermissionSetForm = ({ onSaved }: { onSaved?: () => void }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [form] = useForm();
  const { queryService } = useStack9();
  const { screen } = useScreen();
  const screenKey = ep(screen && screen.head.key);
  const { schema, isValidating } = useEntitySchema('permission_set');

  const handleSave = useCallback(async () => {
    if (isProcessing) return;
    try {
      await form.validateFields(['name']);
    } catch {
      return;
    }
    const values = form.getFieldsValue();
    setIsProcessing(true);
    try {
      await queryService.runNamedQuery(screenKey, 'create_permission_set', {
        vars: { body: values },
      });
      message.success('Permission set created');
      form.resetFields();
      onSaved?.();
    } catch {
      message.error('Failed to create permission set');
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, form, queryService, screenKey, onSaved]);

  return (
    <Form form={form} layout="vertical" loading={isValidating}>
      <Drawer.Header
        title="Create Permission Set"
        showCancel
        extra={
          <S9Space>
            <S9Button
              label="Save"
              onClick={handleSave}
              loading={isProcessing}
              type="primary"
            />
          </S9Space>
        }
      />
      <Drawer.Content>
        {schema && (
          <Fieldset
            context={{ id: undefined }}
            schema={schema}
            formFields={PERMISSION_SET_FIELDS}
          />
        )}
      </Drawer.Content>
    </Form>
  );
};

export const PermissionSetCreateDrawer = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => (
  <ScreenProvider relativePath="/system_administration/permission-set-create-drawer">
    <Drawer open={open} onClose={onClose} width={520} closable>
      <PermissionSetForm onSaved={onClose} />
    </Drawer>
  </ScreenProvider>
);
